import { COOKIE_NAMES, GUEST_PROFILE_GUID, STORE_NAME } from '@intigral/constant';
import { deleteCookie } from '@intigral/utils/lib/browser-storage/cookie';
import { getCookie } from '@intigral/utils/lib/browser-storage/cookie';
import { get, remove, set } from '@intigral/utils/lib/browser-storage/localStorage';
import { profileConstants, GRACE_PERIOD_HOME_POPUP_DISMISSED_KEY } from '../config/constants';
import h from '../helpers/common';
import { store } from '../store';
import { profileInitialize, selectProfileDone } from '../store/reducers/profileSlice';
import { deleteUser } from '../store/reducers/userSlice';
import { getUserInfo, setUser } from '../utils/user';

import { isGuestBrowseProfile, isGuestUser } from './guards';

import type { GuestSessionCookie } from './types';

export function buildGuestBrowseSelectedProfile(kidsMode = false) {
    const rating = kidsMode ? profileConstants.RATING_PG ?? 'PG' : profileConstants.RATING_R18 ?? 'R18';
    return {
        profileGuid: GUEST_PROFILE_GUID,
        profileName: kidsMode ? 'Guest Kids' : 'Guest',
        kidsMode,
        coldStart: 1,
        parentalControl: rating,
        newParentalControl: rating,
        newParenxtalControl: rating,
        parentalControlValues: h.setUserRatingValues(rating),
        defaultSubTitleLang: 'ar',
        defaultAudioLang: 'en',
        autoPlayNextEpisode: true,
        profilePasssword: '',
        showWelcomeScreen: false,
        date: Date.now(),
    };
}

function persistGuestBrowseProfile(profile: Record<string, unknown>): void {
    set(STORE_NAME.SELECTED_PROFILE, profile);
    set(STORE_NAME.USER_PROFILE_LIST, GUEST_PROFILE_LIST);
    h.setContext({ [STORE_NAME.SELECTED_PROFILE]: profile });

    const payload = { list: GUEST_PROFILE_LIST, selected: profile };
    store.dispatch(profileInitialize(payload));
    store.dispatch(selectProfileDone(profile));
}

/** Toggle kids mode on the single guest browse profile. */
export function setGuestKidsMode(kidsMode: boolean) {
    if (!isGuestUser(getUserInfo())) {
        return null;
    }

    const stored = get(STORE_NAME.SELECTED_PROFILE);
    if (isGuestBrowseProfile(stored) && stored.kidsMode === kidsMode) {
        return stored;
    }

    const profile = buildGuestBrowseSelectedProfile(kidsMode);
    persistGuestBrowseProfile(profile);
    return profile;
}

const GUEST_PROFILE_LIST = {
    parentalLockOn: 0,
    profiles: [],
    profilesLimit: 5,
    profileRev: 0,
    date: Date.now(),
};

export function readSessionCookie(): GuestSessionCookie | null {
    const userCookieData = getCookie(COOKIE_NAMES.USER_SESSION);
    if (!userCookieData) {
        return null;
    }
    try {
        return JSON.parse(window.atob(decodeURIComponent(userCookieData))) as GuestSessionCookie;
    } catch {
        return null;
    }
}

export function isSignedInSessionCookie(cookie: GuestSessionCookie | null | undefined): boolean {
    if (!cookie) {
        return false;
    }
    return cookie.isGuest !== true;
}

export function hasSignedInSessionCookieWithoutLocalUser(): boolean {
    const cookie = readSessionCookie();
    const local = getUserInfo();
    return !local?.username && isSignedInSessionCookie(cookie);
}

function expireCookie(name: string, domain?: string, secure = false): void {
    const expire = 'Thu, 01 Jan 1970 00:00:00 GMT';
    const secureSuffix = secure ? '; secure' : '';
    const domainSuffix = domain ? `; domain=${domain}` : '';
    document.cookie = `${name}=; path=/${domainSuffix}; expires=${expire}${secureSuffix}`;
}

export function clearAuthSessionCookies(): void {
    if (typeof window === 'undefined') {
        return;
    }

    const hostname = window.location.hostname;
    const names = [COOKIE_NAMES.USER_SESSION, COOKIE_NAMES.USER_API_KEY, COOKIE_NAMES.USER_AUTH_TOKEN];

    for (const name of names) {
        expireCookie(name);
        expireCookie(name, undefined, true);
        expireCookie(name, `.${hostname}`);
        expireCookie(name, `.${hostname}`, true);
        expireCookie(name, hostname);
        deleteCookie(name);
        deleteCookie(name, true);
    }
}

/** Drop signed-in session cookie when it no longer matches local session (post-logout). */
export function clearStaleSignedInCookieWithoutLocalUser(): void {
    const cookie = readSessionCookie();
    const local = getUserInfo();

    if (!isSignedInSessionCookie(cookie) || !local?.username || isGuestUser(local)) {
        return;
    }
}

function guestProfileIsReady(): boolean {
    const stored = get(STORE_NAME.SELECTED_PROFILE);
    const reduxProfile = (store.getState() as { profile?: { selectedProfile?: { profileGuid?: string } } }).profile
        ?.selectedProfile;
    return isGuestBrowseProfile(stored) && isGuestBrowseProfile(reduxProfile);
}

export function clearSelectedProfileState(): void {
    remove(STORE_NAME.SELECTED_PROFILE);
    remove(STORE_NAME.USER_PROFILE_LIST);
    store.dispatch(profileInitialize({ list: null, selected: undefined }));
}

export function clearGuestBrowseSessionArtifacts(): void {
    remove(STORE_NAME.GUEST_PUBLIC_CONFIG);
}

/** Wipe client auth storage on logout so the next visit can bootstrap cleanly. */
export function clearWcpClientAuthState(options?: { skipRedux?: boolean }): void {
    remove(STORE_NAME.USER);
    remove(STORE_NAME.SELECTED_PROFILE);
    remove(STORE_NAME.USER_PROFILE_LIST);
    remove(GRACE_PERIOD_HOME_POPUP_DISMISSED_KEY);
    clearGuestBrowseSessionArtifacts();
    clearAuthSessionCookies();

    if (options?.skipRedux) {
        return;
    }

    store.dispatch(deleteUser());
    store.dispatch(profileInitialize({ list: null, selected: undefined }));
}

/** Leftover local user without a session cookie (e.g. after logout). */
export function clearOrphanedAuthState(): void {
    const cookie = readSessionCookie();
    const local = getUserInfo();
    if (local?.username && !cookie) {
        clearWcpClientAuthState();
    }
}

export function shouldRunGuestLandingBootstrap(): boolean {
    const cookie = readSessionCookie();
    const local = getUserInfo();

    if (!cookie && !local?.username) {
        return true;
    }

    if (isGuestUser(local)) {
        return false;
    }

    if (cookie?.isGuest === true) {
        return false;
    }

    if (local?.username && cookie) {
        return false;
    }

    if (local?.username && !cookie) {
        clearOrphanedAuthState();
        return true;
    }

    if (!local?.username && cookie) {
        return false;
    }

    return true;
}

export function ensureGuestBrowseProfile() {
    if (!isGuestUser(getUserInfo())) {
        return null;
    }

    if (guestProfileIsReady()) {
        return get(STORE_NAME.SELECTED_PROFILE);
    }

    const stored = get(STORE_NAME.SELECTED_PROFILE);
    const profile = isGuestBrowseProfile(stored) ? stored : buildGuestBrowseSelectedProfile(false);

    persistGuestBrowseProfile(profile);

    return profile;
}

export function onGuestUserSession(): void {
    const stored = get(STORE_NAME.SELECTED_PROFILE);
    if (stored && !isGuestBrowseProfile(stored)) {
        clearSelectedProfileState();
    }
    ensureGuestBrowseProfile();
}

export function syncGuestBrowseProfile() {
    onGuestUserSession();
}

export function onSignedInUserSession(
    user: { isGuest?: boolean } | null | undefined,
    options?: { clearProfile?: boolean }
): void {
    if (isGuestUser(user)) {
        return;
    }
    clearGuestBrowseSessionArtifacts();
    if (options?.clearProfile || isGuestBrowseProfile(get(STORE_NAME.SELECTED_PROFILE))) {
        clearSelectedProfileState();
    }
}

export function mergeSignedInUserSession<T extends { isGuest?: boolean }>(
    existing: T | null | undefined,
    incoming: T
): T {
    const base = isGuestUser(existing) ? ({} as T) : existing ?? ({} as T);
    const merged = { ...base, ...incoming };
    delete merged.isGuest;
    return merged;
}

export function buildGuestUserFromSessionCookie(data: GuestSessionCookie) {
    const stored = getUserInfo();
    const now = Date.now();
    const apiKey =
        data.a ??
        (typeof stored?.apiKey === 'string' ? stored.apiKey : undefined) ??
        getCookie(COOKIE_NAMES.USER_API_KEY) ??
        process.env.NX_PUBLIC_WCP_STATIC_API_KEY ??
        '';

    return {
        ...(isGuestUser(stored) ? stored : {}),
        isGuest: true,
        username: data.u,
        authToken: data.t,
        identityToken: data.i,
        authSign: data.s,
        apiKey,
        createdTime: now,
        firstLoginDate: stored?.firstLoginDate ?? now,
        operator: stored?.operator ?? 'DEFAULT',
        segment: stored?.segment ?? 'B2C',
        subscriptionStatus: stored?.subscriptionStatus ?? [],
    };
}
